import React, { useState } from "react";
import {
  GitBranch,
  RefreshCw,
  Pencil,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import toast from "react-hot-toast";
import IngressYAMLEditor from "./IngressYAMLEditor";
import tenantService from "../../services/tenant.service";

interface IngressHistory {
  commit_id: string;
  status: string;
  timestamp: string;
  action: string;
}

interface IngressData {
  commit_id: string;
  status: string;
  updated_at: string;
  history: IngressHistory[];
  yaml?: string;
}

interface IngressManagementPanelProps {
  tenantKey: string;
  ingress: IngressData | null;
  loading?: boolean;
  onRefresh: () => void;
}

const STATUS_CONFIG: Record<string, { icon: React.ReactNode; label: string; className: string }> = {
  SUCCESS: {
    icon: <CheckCircle2 className="w-4 h-4" />,
    label: "Deployed",
    className: "text-emerald-600 bg-emerald-50 border-emerald-200",
  },
  RUNNING: {
    icon: <Loader2 className="w-4 h-4 animate-spin" />,
    label: "Running",
    className: "text-blue-600 bg-blue-50 border-blue-200",
  },
  FAILED: {
    icon: <XCircle className="w-4 h-4" />,
    label: "Failed",
    className: "text-red-600 bg-red-50 border-red-200",
  },
  PENDING: {
    icon: <Clock className="w-4 h-4" />,
    label: "Pending",
    className: "text-amber-600 bg-amber-50 border-amber-200",
  },
};

const MOCK_YAML = `apiVersion: networking.k8s.io/v1
kind: Ingress
metadata:
  name: {{tenantKey}}-ingress
  namespace: {{tenantKey}}
  annotations:
    kubernetes.io/ingress.class: nginx
    cert-manager.io/cluster-issuer: letsencrypt-prod
spec:
  tls:
    - hosts:
        - app.{{tenantKey}}.dociq.com
      secretName: {{tenantKey}}-tls
  rules:
    - host: app.{{tenantKey}}.dociq.com
      http:
        paths:
          - path: /
            pathType: Prefix
            backend:
              service:
                name: {{tenantKey}}-web
                port:
                  number: 80`;

const IngressManagementPanel: React.FC<IngressManagementPanelProps> = ({
  tenantKey,
  ingress,
  loading = false,
  onRefresh,
}) => {
  const [yamlEditorOpen, setYamlEditorOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(false);

  const resolvedYAML = (ingress?.yaml ?? MOCK_YAML).replace(/\{\{tenantKey\}\}/g, tenantKey);

  const handleSync = async () => {
    setSyncing(true);
    // TODO: replace with tenantService.syncIngress(tenantKey)
    await new Promise((r) => setTimeout(r, 1000));
    setSyncing(false);
    toast.success("Ingress sync triggered");
    onRefresh();
  };

  const handleYAMLSave = async (yaml: string, commitMessage: string): Promise<void> => {
    const res = await tenantService.updateIngressYAML({ yaml, commit_message: commitMessage });
    setYamlEditorOpen(false);
    toast.success(`YAML committed: ${res.data.commit_id.slice(0, 8)}`);
    onRefresh();
  };

  const statusKey = (ingress?.status ?? "").toUpperCase();
  const statusCfg = STATUS_CONFIG[statusKey] ?? STATUS_CONFIG["PENDING"];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Status bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-semibold ${statusCfg.className}`}
          >
            {statusCfg.icon}
            {statusCfg.label}
          </span>
          {ingress?.commit_id && (
            <span className="flex items-center gap-1.5 text-xs text-slate-500">
              <GitBranch className="w-3.5 h-3.5" />
              <span className="font-mono">{ingress.commit_id.slice(0, 8)}</span>
            </span>
          )}
          {ingress?.updated_at && (
            <span className="text-xs text-slate-400">
              Updated {new Date(ingress.updated_at).toLocaleString()}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={onRefresh}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-all disabled:opacity-50"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            onClick={handleSync}
            disabled={syncing}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-all disabled:opacity-50"
          >
            {syncing ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <RefreshCw className="w-3 h-3" />
            )}
            Sync
          </button>
          <button
            onClick={() => setYamlEditorOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-all"
          >
            <Pencil className="w-3 h-3" />
            Edit YAML
          </button>
        </div>
      </div>

      {/* YAML viewer */}
      <div className="rounded-lg border border-slate-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2 bg-slate-800 border-b border-slate-700">
          <span className="text-xs font-semibold text-slate-300 font-mono">ingress.yaml</span>
          <span className="text-xs text-slate-500">read-only preview</span>
        </div>
        <pre className="px-4 py-4 bg-slate-900 text-xs font-mono text-green-300 leading-relaxed overflow-x-auto max-h-72 overflow-y-auto">
          {resolvedYAML}
        </pre>
      </div>

      {/* Deployment history */}
      {ingress?.history && ingress.history.length > 0 && (
        <div>
          <button
            onClick={() => setHistoryExpanded((v) => !v)}
            className="flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-slate-900 transition-colors"
          >
            {historyExpanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
            Deployment History ({ingress.history.length})
          </button>

          {historyExpanded && (
            <div className="mt-3 space-y-2">
              {ingress.history.map((h, i) => {
                const hStatus = (h.status ?? "").toUpperCase();
                const hCfg = STATUS_CONFIG[hStatus] ?? STATUS_CONFIG["PENDING"];
                return (
                  <div
                    key={i}
                    className="flex items-center gap-3 px-4 py-3 bg-slate-50 rounded-lg border border-slate-100"
                  >
                    <span className={`flex items-center gap-1 text-xs font-semibold ${hCfg.className} px-2 py-0.5 rounded border`}>
                      {hCfg.icon}
                      {hCfg.label}
                    </span>
                    <span className="flex items-center gap-1.5 text-xs text-slate-500">
                      <GitBranch className="w-3 h-3" />
                      <span className="font-mono">{h.commit_id.slice(0, 8)}</span>
                    </span>
                    <span className="text-xs text-slate-500 capitalize">{h.action}</span>
                    <span className="ml-auto text-xs text-slate-400">
                      {new Date(h.timestamp).toLocaleString()}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <IngressYAMLEditor
        isOpen={yamlEditorOpen}
        initialYAML={resolvedYAML}
        tenantKey={tenantKey}
        onClose={() => setYamlEditorOpen(false)}
        onSuccess={handleYAMLSave}
      />
    </div>
  );
};

export default IngressManagementPanel;
